import { useEffect, useRef, useState } from "react";

// Horizontal stops as % of the cards container width.
// -1 = home pedestal, 0/1/2 = above each card.
const POS = { "-1": 7, 0: 17, 1: 50, 2: 83 };
const SEQUENCE = [-1, 0, 1, 2, -1];

export const MascotTour = ({ steps }) => {
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const idxRef = useRef(0);
  const [stop, setStop] = useState(-1);
  const [dir, setDir] = useState(0); // -1 left, 1 right
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([e]) => setRunning(e.isIntersecting),
      { threshold: 0.35 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!running) {
      clearInterval(timerRef.current);
      return;
    }
    const advance = () => {
      const prev = SEQUENCE[idxRef.current];
      idxRef.current = (idxRef.current + 1) % SEQUENCE.length;
      const next = SEQUENCE[idxRef.current];
      setDir(POS[next] > POS[prev] ? 1 : POS[next] < POS[prev] ? -1 : 0);
      setStop(next);
    };
    timerRef.current = setInterval(advance, 1700);
    return () => clearInterval(timerRef.current);
  }, [running]);

  return (
    <div ref={wrapRef} className="relative pt-24 lg:pt-28">
      {/* Home pedestal */}
      <div
        className="hidden lg:block absolute -top-2 z-0"
        style={{ left: `${POS["-1"]}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-24 h-3 rounded-full bg-[#6043EF]/15 blur-[2px]" />
        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#6043EF] text-center font-['IBM_Plex_Mono'] whitespace-nowrap">
          Your Guide
        </p>
      </div>

      {/* Travelling mascot bubble (lg+) */}
      <div
        className="hidden lg:block absolute top-0 z-30 pointer-events-none transition-[left] duration-[1100ms] ease-in-out"
        style={{ left: `${POS[stop]}%`, transform: "translateX(-50%)" }}
        data-testid="mascot-tour-bubble"
      >
        <div
          className="relative transition-transform duration-500"
          style={{ transform: `rotate(${dir * 5}deg)` }}
        >
          {/* shadow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-16 h-3 rounded-full bg-black/20 blur-md" />
          <div
            className="w-[92px] h-[92px] rounded-full overflow-hidden border-[3px] border-white ring-2 ring-[#6043EF]/40 shadow-[0_14px_30px_rgba(74,40,194,0.4)] bg-[#EDE9FE]"
            style={{ animation: "mascotWalk 0.6s ease-in-out infinite" }}
          >
            <img
              src="/mascot-badge.png"
              alt="NEET guide mascot"
              className="w-full h-full object-cover scale-110"
            />
          </div>
          {/* speech pip when pointing at a card */}
          {stop >= 0 && (
            <div className="absolute -right-1 -top-1 w-7 h-7 rounded-full bg-[#FDE047] border-2 border-black flex items-center justify-center text-[11px] font-extrabold animate-fade-in">
              {stop + 1}
            </div>
          )}
        </div>
      </div>

      {/* Progressive step cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative z-10">
        {steps.map((item, i) => {
          const active = stop === i;
          const tex = item.texture;
          const scrim = tex ? (tex.mode === "light" ? "bg-black/35" : "bg-white/45") : "";
          return (
            <div
              key={i}
              className={`group relative overflow-hidden rounded-2xl p-6 ${tex ? "" : `bg-gradient-to-br ${item.tone} text-[#1E1B4B]`} border border-violet-200/70 shadow-soft transition-all duration-500 animate-fade-in stagger-${i + 1}`}
              style={{
                marginTop: `${i * 14}px`,
                transform: active ? "translateY(-10px) scale(1.03)" : "none",
                boxShadow: active ? "0 28px 55px rgba(96,67,239,0.28)" : undefined,
              }}
              data-testid={`step-card-${item.step}`}
            >
              {/* Texture background layer */}
              {tex && (
                <>
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{ backgroundImage: `url(${tex.url})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.8 }}
                    aria-hidden="true"
                  />
                  <div className={`pointer-events-none absolute inset-0 ${scrim}`} aria-hidden="true" />
                </>
              )}
              {/* idle shine sweep on hover */}
              <div className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/70 to-transparent" />
              {/* active glow sweep when mascot points */}
              {active && (
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent" style={{ animation: "shineSweep 1s ease-out" }} />
              )}
              <div className="relative" style={tex ? { color: tex.color, textShadow: tex.shadow } : undefined}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-['IBM_Plex_Mono'] text-3xl font-extrabold" style={{ color: tex ? tex.color : "#6043EF" }}>{item.step}</span>
                  <span className={`h-9 w-9 rounded-full border flex items-center justify-center text-sm font-bold transition-colors ${active ? "bg-[#FDE047] text-black border-black" : "bg-white text-[#6043EF] border-violet-200"}`}>
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-xl font-bold tracking-tight font-heading mb-2" style={tex ? { color: tex.color } : { color: "#1E1B4B" }}>{item.title}</h3>
                <p className="text-sm font-sans leading-relaxed" style={tex ? { color: tex.color } : { color: "#52525B" }}>{item.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Static mascot for mobile / reduced-motion */}
      <div className="lg:hidden flex justify-center mt-8">
        <div className="w-24 h-24 rounded-full overflow-hidden border-[3px] border-white ring-2 ring-[#6043EF]/40 shadow-[0_14px_30px_rgba(74,40,194,0.4)]">
          <img src="/mascot-badge.png" alt="NEET guide mascot" className="w-full h-full object-cover scale-110" />
        </div>
      </div>
    </div>
  );
};
